import type { ProductStatus } from '~/schemas/shopify/admin.types';
import type { IPickPageMode } from '../types';

import {
  PICK_PAGE_SEARCH_TERM_SEPARATOR_PATTERN,
  PICK_PAGE_QUERYABLE_STATUSES,
} from '../constants';
import { toPickPageSearchSubject } from './pickPageSearch';

const toSearchTerms = (searchValue: string): string[] =>
  toPickPageSearchSubject(searchValue)
    .split(PICK_PAGE_SEARCH_TERM_SEPARATOR_PATTERN)
    .filter(Boolean);

// Every term must hit either the title or the handle, so "gift card" matches both a
// "Gift card" product and a `/products/gift-card` URL. Shopify matches case-insensitively.
const toTermsQuery = (terms: string[]): string =>
  terms.map((term) => `(title:*${term}* OR handle:*${term}*)`).join(' AND ');

/**
 * The platform search a listing is narrowed by, for the page types that filter on nothing else.
 * Undefined asks for no filtering at all, which is what an empty term means.
 */
export const toPickPageSearchQuery = (searchValue: string): string | undefined => {
  const terms = toSearchTerms(searchValue);
  if (!terms.length) return undefined;

  return toTermsQuery(terms);
};

const toStatusQuery = (statuses: ProductStatus[]): string =>
  `(${statuses.map((status) => `status:${status.toLowerCase()}`).join(' OR ')})`;

interface IToProductSearchQueryParams {
  searchValue: string;
  /** What the pick is for — see `PICK_PAGE_QUERYABLE_STATUSES` for the statuses each mode accepts. */
  mode: IPickPageMode;
  isControl: boolean;
}

// A product listing always carries a status filter, so unlike the other types it queries even for
// an empty term.
export const toProductSearchQuery = ({
  searchValue,
  mode,
  isControl,
}: IToProductSearchQueryParams): string => {
  const statusQuery = toStatusQuery(
    PICK_PAGE_QUERYABLE_STATUSES[mode][isControl ? 'control' : 'variant'],
  );
  const terms = toSearchTerms(searchValue);
  if (!terms.length) return statusQuery;

  return `${statusQuery} AND ${toTermsQuery(terms)}`;
};
